import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getLetras, deleteLetra, updateLetra, addPagoToLetra, getCuentas } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { FileCheck, Trash2, CreditCard, PlusCircle, Pencil, Download, FileSpreadsheet } from 'lucide-react';
import api from '../services/api';

const ESTADO_COLORS = {
  ACTIVA: 'bg-yellow-100 text-yellow-700',
  PAGADA: 'bg-emerald-100 text-emerald-700',
  ANULADA: 'bg-red-100 text-red-700',
  RENOVADA: 'bg-blue-100 text-blue-700'
};

const MEDIOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
  { value: 'DEPOSITO', label: 'Depósito' },
];

const LetrasPage = () => {
  const { empresaActual } = useApp();
  const [letras, setLetras] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('ACTIVA'); // Por defecto solo activas
  const [filtroNroLetra, setFiltroNroLetra] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  const [exportando, setExportando] = useState(false);
  
  // Modal agregar pago
  const [agregarPagoOpen, setAgregarPagoOpen] = useState(false);
  const [selectedLetra, setSelectedLetra] = useState(null);
  const [nuevoPago, setNuevoPago] = useState({
    cuenta_id: '',
    medio_pago: 'TRANSFERENCIA',
    monto: '',
    ref_pago: ''
  });
  const [savingPago, setSavingPago] = useState(false);

  // Modal editar letra
  const [editarOpen, setEditarOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    nro_letra: '',
    fecha_venc: '',
    monto_me: '',
    nro_unico_banco: '',
    cuenta_pago_id: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // Modal confirmar eliminación
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [letraToDelete, setLetraToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const [letrasRes, cuentasRes] = await Promise.all([
        getLetras(empresaActual.id, undefined, filtroEstado === 'all' ? undefined : filtroEstado),
        getCuentas(empresaActual.id)
      ]);
      setLetras(letrasRes.data);
      setCuentas(cuentasRes.data.filter(c => c.activo));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, filtroEstado]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = (letra) => {
    setLetraToDelete(letra);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!letraToDelete) return;
    setDeleting(true);
    try {
      await deleteLetra(letraToDelete.id);
      toast.success('Letra eliminada. El monto fue devuelto a la CxP.');
      setDeleteConfirmOpen(false);
      setLetraToDelete(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar la letra');
    } finally {
      setDeleting(false);
    }
  };

  // Abrir modal editar
  const handleEditar = (letra) => {
    setSelectedLetra(letra);
    setEditForm({
      nro_letra: letra.nro_letra,
      fecha_venc: letra.fecha_venc,
      monto_me: letra.monto_me.toString(),
      nro_unico_banco: letra.nro_unico_banco || '',
      cuenta_pago_id: letra.cuenta_pago_id?.toString() || ''
    });
    setEditarOpen(true);
  };

  // Guardar edición
  const handleGuardarEdit = async () => {
    if (!editForm.nro_letra || !editForm.fecha_venc || !editForm.monto_me) {
      toast.error('Complete todos los campos obligatorios');
      return;
    }
    setSavingEdit(true);
    try {
      await updateLetra(selectedLetra.id, {
        nro_letra: editForm.nro_letra,
        fecha_venc: editForm.fecha_venc,
        monto_me: parseFloat(editForm.monto_me),
        nro_unico_banco: editForm.nro_unico_banco || null,
        cuenta_pago_id: editForm.cuenta_pago_id ? parseInt(editForm.cuenta_pago_id) : null
      });
      toast.success('Letra actualizada');
      setEditarOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSavingEdit(false);
    }
  };

  // Actualización inline del N° Único Banco
  const handleUpdateNroUnicoBanco = async (letraId, nuevoValor) => {
    try {
      const letra = letras.find(l => l.id === letraId);
      await updateLetra(letraId, {
        nro_letra: letra.nro_letra,
        fecha_venc: letra.fecha_venc,
        monto_me: letra.monto_me,
        nro_unico_banco: nuevoValor || null,
        cuenta_pago_id: letra.cuenta_pago_id
      });
      toast.success('N° Único Banco actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleAgregarPago = (letra) => {
    setSelectedLetra(letra);
    setNuevoPago({
      cuenta_id: cuentas[0]?.id?.toString() || '',
      medio_pago: 'TRANSFERENCIA',
      monto: letra.saldo_me.toFixed(2),
      ref_pago: `Pago de letra ${letra.nro_letra}`
    });
    setAgregarPagoOpen(true);
  };

  const handleGuardarPago = async () => {
    if (!nuevoPago.cuenta_id || !nuevoPago.monto || parseFloat(nuevoPago.monto) <= 0) {
      toast.error('Complete los datos del pago');
      return;
    }
    setSavingPago(true);
    try {
      await addPagoToLetra(selectedLetra.id, {
        cuenta_id: parseInt(nuevoPago.cuenta_id),
        medio_pago: nuevoPago.medio_pago,
        monto: parseFloat(nuevoPago.monto),
        ref_pago: nuevoPago.ref_pago
      });
      toast.success('Pago registrado');
      setAgregarPagoOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setSavingPago(false);
    }
  };

  // Filtrar letras
  const letrasFiltradas = letras.filter(letra => {
    if (filtroNroLetra) {
      const nroLower = filtroNroLetra.toLowerCase();
      if (!(letra.nro_letra || '').toLowerCase().includes(nroLower) && 
          !(letra.nro_unico_banco || '').toLowerCase().includes(nroLower)) {
        return false;
      }
    }
    if (filtroProveedor) {
      const provLower = filtroProveedor.toLowerCase();
      if (!(letra.proveedor_nombre || '').toLowerCase().includes(provLower)) {
        return false;
      }
    }
    return true;
  });

  // Exportar a Excel
  const handleExportExcel = async () => {
    setExportando(true);
    try {
      const response = await api.get(`/letras/export/excel?empresa_id=${empresaActual.id}${filtroEstado !== 'all' ? `&estado=${filtroEstado}` : ''}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `letras_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel descargado');
    } catch (error) {
      toast.error('Error al exportar Excel');
    } finally {
      setExportando(false);
    }
  };

  // Exportar a PDF
  const handleExportPDF = async () => {
    setExportando(true);
    try {
      const response = await api.get(`/letras/export/pdf?empresa_id=${empresaActual.id}${filtroEstado !== 'all' ? `&estado=${filtroEstado}` : ''}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `letras_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al exportar PDF');
    } finally {
      setExportando(false);
    }
  };

  const totalLetras = letrasFiltradas.reduce((sum, l) => sum + l.monto_me, 0);
  const totalSaldo = letrasFiltradas.reduce((sum, l) => sum + l.saldo_me, 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileCheck className="w-7 h-7 text-blue-600" />
            Letras por Pagar
          </h1>
          <p className="text-gray-500">Gestiona tus letras de cambio</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportExcel} disabled={exportando}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button variant="outline" onClick={handleExportPDF} disabled={exportando}>
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm text-gray-600">N° Letra / N° Único Banco</Label>
            <Input
              placeholder="Buscar por número..."
              value={filtroNroLetra}
              onChange={(e) => setFiltroNroLetra(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Proveedor</Label>
            <Input
              placeholder="Buscar por proveedor..."
              value={filtroProveedor}
              onChange={(e) => setFiltroProveedor(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Estado</Label>
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Todos los estados" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="ACTIVA">Activa</SelectItem>
                <SelectItem value="PAGADA">Pagada</SelectItem>
                <SelectItem value="ANULADA">Anulada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setFiltroNroLetra('');
                setFiltroProveedor('');
                setFiltroEstado('all');
              }}
              className="w-full"
            >
              Limpiar filtros
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-500">Total Letras</p>
          <p className="text-2xl font-bold">{letras.length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-500">Monto Total</p>
          <p className="text-2xl font-bold text-blue-600">S/. {totalLetras.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-500">Saldo Pendiente</p>
          <p className="text-2xl font-bold text-orange-600">S/. {totalSaldo.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>N° Letra</TableHead>
                <TableHead>N° Único Banco</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Banco Pago</TableHead>
                <TableHead>Vencimiento</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">Cargando...</TableCell></TableRow>
              ) : letrasFiltradas.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">{letras.length === 0 ? 'No hay letras' : 'No se encontraron resultados'}</TableCell></TableRow>
              ) : letrasFiltradas.map(letra => (
                <TableRow key={letra.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium font-mono">{letra.nro_letra}</TableCell>
                  <TableCell>
                    <Input
                      className="h-8 font-mono text-blue-600 w-32"
                      defaultValue={letra.nro_unico_banco || ''}
                      placeholder="Ingresar..."
                      onBlur={(e) => {
                        if (e.target.value !== (letra.nro_unico_banco || '')) {
                          handleUpdateNroUnicoBanco(letra.id, e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>{letra.proveedor_nombre || '-'}</TableCell>
                  <TableCell className="text-sm">{letra.cuenta_nombre || <span className="text-gray-400">-</span>}</TableCell>
                  <TableCell className={letra.estado === 'ACTIVA' ? 'text-orange-600 font-medium' : ''}>{letra.fecha_venc}</TableCell>
                  <TableCell className="text-right font-medium">{letra.moneda_codigo} {letra.monto_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell className={`text-right font-medium ${letra.saldo_me > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>
                    {letra.moneda_codigo} {letra.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell><Badge className={ESTADO_COLORS[letra.estado]}>{letra.estado}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {/* Editar - siempre disponible para letras activas */}
                      {letra.estado === 'ACTIVA' && (
                        <Button variant="ghost" size="sm" onClick={() => handleEditar(letra)} className="text-blue-600" title="Editar">
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Pagar */}
                      {letra.saldo_me > 0.01 && letra.estado === 'ACTIVA' && (
                        <Button variant="ghost" size="sm" onClick={() => handleAgregarPago(letra)} className="text-emerald-600" title="Pagar">
                          <CreditCard className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Eliminar - solo si no tiene pagos (saldo igual a monto) */}
                      {Math.abs(letra.saldo_me - letra.monto_me) < 0.01 && letra.estado === 'ACTIVA' && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(letra)} className="text-red-500" title="Eliminar letra">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal Editar Letra */}
      <Dialog open={editarOpen} onOpenChange={setEditarOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Editar Letra
            </DialogTitle>
          </DialogHeader>
          {selectedLetra && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                <p>Proveedor: {selectedLetra.proveedor_nombre}</p>
                <p className="text-xs mt-1">Al cambiar el monto, se ajustará automáticamente el saldo de la CxP</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>N° Letra *</Label>
                  <Input
                    className="mt-1 font-mono"
                    value={editForm.nro_letra}
                    onChange={(e) => setEditForm({...editForm, nro_letra: e.target.value})}
                    placeholder="L001-00001"
                  />
                </div>
                <div>
                  <Label>N° Único Banco</Label>
                  <Input
                    className="mt-1 font-mono"
                    value={editForm.nro_unico_banco}
                    onChange={(e) => setEditForm({...editForm, nro_unico_banco: e.target.value})}
                    placeholder="Número del banco"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Vencimiento *</Label>
                  <Input
                    type="date"
                    className="mt-1"
                    value={editForm.fecha_venc}
                    onChange={(e) => setEditForm({...editForm, fecha_venc: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Banco Pago</Label>
                  <Select value={editForm.cuenta_pago_id || "none"} onValueChange={(v) => setEditForm({...editForm, cuenta_pago_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleccionar banco" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {cuentas.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>
                          {c.nombre} ({c.moneda_codigo})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Monto *</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="mt-1"
                  value={editForm.monto_me}
                  onChange={(e) => setEditForm({...editForm, monto_me: e.target.value})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditarOpen(false)}>Cancelar</Button>
            <Button onClick={handleGuardarEdit} disabled={savingEdit} className="bg-blue-600 hover:bg-blue-700">
              {savingEdit ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Pagar Letra */}
      <Dialog open={agregarPagoOpen} onOpenChange={setAgregarPagoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-emerald-600" />
              Pagar Letra
            </DialogTitle>
          </DialogHeader>
          {selectedLetra && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium font-mono">{selectedLetra.nro_letra}</p>
                    <p className="text-sm text-gray-500">{selectedLetra.proveedor_nombre}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Saldo</p>
                    <p className="font-bold text-lg text-orange-600">{selectedLetra.moneda_codigo} {selectedLetra.saldo_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <Label>Cuenta *</Label>
                  <Select value={nuevoPago.cuenta_id} onValueChange={(v) => setNuevoPago({...nuevoPago, cuenta_id: v})}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                    <SelectContent>
                      {cuentas.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.nombre} ({c.moneda_codigo})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Medio de Pago *</Label>
                  <Select value={nuevoPago.medio_pago} onValueChange={(v) => setNuevoPago({...nuevoPago, medio_pago: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MEDIOS_PAGO.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Monto *</Label>
                  <Input type="number" step="0.01" className="mt-1" value={nuevoPago.monto} onChange={(e) => setNuevoPago({...nuevoPago, monto: e.target.value})} />
                </div>
                <div>
                  <Label>Referencia</Label>
                  <Input className="mt-1" value={nuevoPago.ref_pago} onChange={(e) => setNuevoPago({...nuevoPago, ref_pago: e.target.value})} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAgregarPagoOpen(false)}>Cancelar</Button>
            <Button onClick={handleGuardarPago} disabled={savingPago} className="bg-emerald-600 hover:bg-emerald-700">
              {savingPago ? 'Guardando...' : 'Registrar Pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmar Eliminación */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmar Eliminación
            </DialogTitle>
          </DialogHeader>
          {letraToDelete && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">¿Estás seguro de eliminar esta letra?</p>
                <div className="bg-white rounded p-3 border">
                  <p className="font-mono font-bold text-lg">{letraToDelete.nro_letra}</p>
                  <p className="text-sm text-gray-500">Proveedor: {letraToDelete.proveedor_nombre}</p>
                  <p className="text-sm text-gray-500">Monto: {letraToDelete.moneda_codigo} {letraToDelete.monto_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> El monto de esta letra será devuelto a la Cuenta por Pagar (CxP) original. Podrás crear nuevas letras con ese saldo.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? 'Eliminando...' : 'Sí, Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LetrasPage;
